/**
 * DRPX Courier — Account-Verwaltung / DSGVO Art. 17 (Supabase)
 *
 * Wrapper für die Edge Function `delete-account`. Die Function kennt vier
 * Actions: `soft`, `restore`, `hard`, `process_pending`. Im Frontend
 * brauchen wir `soft` (User), `restore` (User) und `hard` (Admin).
 *
 * Das User-JWT wird automatisch von Supabase JS aus der laufenden Session
 * in den Request-Header gesetzt.
 *
 * @module lib/supabase/account
 */

import { _sb } from './client.js';

// ════════════════════════════════════════════════════════════════════
// Interne Helper
// ════════════════════════════════════════════════════════════════════

const NO_CLIENT_ERROR = 'Supabase client not initialized';

/**
 * Extrahiert eine Fehler-Nachricht aus einem `unknown`-Wert.
 */
function errMsg(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

/**
 * Parst die Edge-Function-Response, die entweder als String (JSON) oder
 * als bereits geparstes Objekt ankommen kann.
 */
function parseData(raw: unknown): Record<string, unknown> | null {
  if (!raw) return null;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as Record<string, unknown>;
    } catch {
      return null;
    }
  }
  if (typeof raw === 'object') return raw as Record<string, unknown>;
  return null;
}

/**
 * Versucht, den JSON-Body aus einem Supabase FunctionsHttpError-Context
 * zu lesen (z.B. 409 active_orders, 403 Nur Admins). Falls der Context
 * kein `.json()` hat oder das Parsen fehlschlägt, wird `null` zurückgegeben.
 */
async function parseErrorContext(
  error: { context?: unknown; message?: string },
): Promise<Record<string, unknown> | null> {
  try {
    const ctx = error.context;
    if (ctx && typeof ctx === 'object' && 'json' in ctx) {
      const jsonFn = (ctx as { json: () => Promise<unknown> }).json;
      if (typeof jsonFn === 'function') {
        const parsed = await jsonFn.call(ctx);
        return parsed && typeof parsed === 'object'
          ? (parsed as Record<string, unknown>)
          : null;
      }
    }
  } catch {
    /* ignore */
  }
  return null;
}

// ════════════════════════════════════════════════════════════════════
// Lokale Result-Typen (nicht exportiert)
// ════════════════════════════════════════════════════════════════════

/**
 * Result von `softDeleteAccount`. Bei `active_orders`-Error enthält das
 * Objekt die Liste der blockierenden Aufträge.
 */
interface SoftDeleteResult {
  ok?: true;
  error?: string;
  activeOrders?: Array<{ nr?: string; auftrag_id?: string }>;
}

/**
 * Result von `restoreAccount`.
 */
interface RestoreResult {
  ok?: true;
  error?: string;
}

/**
 * Data-Payload von `hardDeleteAccount` bei Erfolg.
 */
interface HardDeleteData {
  warnings: string[];
}

// ════════════════════════════════════════════════════════════════════
// ACCOUNT OPERATIONS
// ════════════════════════════════════════════════════════════════════

/**
 * Markiert das eigene Konto zur Löschung. Beginnt 30-Tage-Karenzzeit.
 * Wenn der User noch aktive Aufträge hat, wird die Löschung blockiert
 * und die Liste der aktiven Aufträge zurückgegeben.
 *
 * Wird direkt aufgerufen (kein `api()`-Wrapper) — der Caller in
 * `ProfilSeite.jsx` liest `res.error`, `res.activeOrders` und `res.ok`.
 */
export async function softDeleteAccount(grund?: string): Promise<SoftDeleteResult> {
  if (!_sb) return { error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const res = await client.functions.invoke('delete-account', {
      body: JSON.stringify({ action: 'soft', grund: grund || '' }),
    });
    if (res.error) {
      // Supabase wirft bei non-2xx einen Error mit context.
      // Wir versuchen, den Body trotzdem zu lesen (active_orders → 409).
      const parsed = await parseErrorContext(res.error);
      if (parsed && parsed.error === 'active_orders') {
        const active = Array.isArray(parsed.active) ? parsed.active : [];
        return {
          error: 'active_orders',
          activeOrders: active as Array<{ nr?: string; auftrag_id?: string }>,
        };
      }
      return {
        error:
          (parsed && typeof parsed.error === 'string' ? parsed.error : null) ||
          res.error.message ||
          String(res.error),
      };
    }
    const data = parseData(res.data);
    if (data && data.error) return { error: String(data.error) };
    return { ok: true };
  } catch (e: unknown) {
    return { error: errMsg(e) };
  }
}

/**
 * Widerruft die Löschung des eigenen Kontos innerhalb der 30-Tage-Karenz.
 *
 * Wird direkt aufgerufen (kein `api()`-Wrapper) — der Caller in
 * `PendingDeletionScreen.jsx` liest `res.ok` und `res.error`.
 */
export async function restoreAccount(): Promise<RestoreResult> {
  if (!_sb) return { error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const res = await client.functions.invoke('delete-account', {
      body: JSON.stringify({ action: 'restore' }),
    });
    if (res.error) return { error: res.error.message || String(res.error) };
    const data = parseData(res.data);
    if (data && data.error) return { error: String(data.error) };
    return { ok: true };
  } catch (e: unknown) {
    return { error: errMsg(e) };
  }
}

/**
 * Admin-only: Löscht ein fremdes Konto sofort und endgültig.
 *
 * Ruft die Edge Function `delete-account` mit `action='hard'` auf. Die
 * Function prüft serverseitig, ob der Caller wirklich Admin ist (Rolle
 * wird aus der `profiles`-Tabelle gelesen, NICHT aus JWT-Metadata).
 *
 * Räumt auf: 16 Tabellen + 5 Storage-Buckets + auth.users-Eintrag.
 * `auftraege` werden pseudonymisiert statt gelöscht (DAC7: 6 Jahre
 * Aufbewahrungspflicht).
 *
 * Wird über `api()` aufgerufen — daher `{data, error}`-Shape.
 *
 * **Bug-Fix gegenüber JS-Version:** Die alte Version gab `{ok, warnings}`
 * zurück, was `api()` nicht verstand (`res.data` war undefined → Caller
 * bekam immer `null` → Erfolgs-Toast und Warnings waren toter Code).
 * Jetzt: `{data: {warnings}, error}` — `api()` gibt `{warnings}` zurück,
 * und der Caller kann `res.warnings` korrekt auslesen.
 */
export async function hardDeleteAccount(
  targetUserId: string,
): Promise<{ data: HardDeleteData | null; error: string | null }> {
  if (!_sb) return { data: null, error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const res = await client.functions.invoke('delete-account', {
      body: JSON.stringify({ action: 'hard', target_user_id: targetUserId }),
    });
    if (res.error) {
      // Body aus dem Fehler-Context lesen (403 Nur Admins / 400 target fehlt)
      const parsed = await parseErrorContext(res.error);
      return {
        data: null,
        error:
          (parsed && typeof parsed.error === 'string' ? parsed.error : null) ||
          res.error.message ||
          String(res.error),
      };
    }
    const data = parseData(res.data);
    if (data && data.error) return { data: null, error: String(data.error) };
    const warnings = Array.isArray(data?.warnings)
      ? (data.warnings as string[])
      : [];
    return { data: { warnings }, error: null };
  } catch (e: unknown) {
    return { data: null, error: errMsg(e) };
  }
}

// ════════════════════════════════════════════════════════════════════
// DSGVO Art. 15 / Art. 20 — Datenexport
// ════════════════════════════════════════════════════════════════════

/**
 * Result von `exportMyData`. Bei Erfolg enthält `data` das vollständige
 * JSON-Objekt wie von der Edge Function geliefert (Drittpersonen-Daten
 * in `auftraege` sind herausgefiltert, siehe export-my-data/index.ts H3).
 */
interface ExportMyDataResult {
  data: Record<string, unknown> | null;
  error: string | null;
}

/**
 * Lädt alle personenbezogenen Daten des eingeloggten Users via die
 * Edge Function `export-my-data`. Erfüllt DSGVO Art. 15 (Auskunft) und
 * Art. 20 (Datenübertragbarkeit).
 *
 * Block 2e Teil 2c K1 (21.04.2026):
 * Vorher hat das Frontend einen client-seitigen Export aus React-State
 * gemacht — der war nur ein Ausschnitt der tatsächlichen Daten
 * (fehlten: fahrer_*, firma_adressbuch, auftrag_nachrichten, notifications,
 * audit_log, viele Profil-Felder). DSGVO-Compliance-Lücke.
 *
 * Die Edge Function macht:
 *   - Rate-Limit (1 Export/Stunde)
 *   - Spalten-Whitelist für das Profil (keine admin_notiz etc.)
 *   - Drittpersonen-Daten aus auftraege entfernt
 *   - Fahrer-Aufträge über angebote-Join ergänzt
 *   - Chat-Nachrichten: gesendet + empfangen
 *   - Audit-Log-Eintrag
 *
 * Der Caller ist dafür verantwortlich, das `data`-JSON als Datei für den
 * User verfügbar zu machen (z.B. als Download).
 *
 * Wird direkt aufgerufen (kein `api()`-Wrapper) — der Caller liest
 * `res.data` und `res.error`.
 */
export async function exportMyData(): Promise<ExportMyDataResult> {
  if (!_sb) return { data: null, error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const res = await client.functions.invoke('export-my-data', {
      // Kein Body nötig — Edge Function liest die User-ID aus dem JWT.
      // GET wäre semantisch passender, aber Supabase-functions-invoke
      // unterstützt nur POST.
      body: {},
    });
    if (res.error) {
      const parsed = await parseErrorContext(res.error);
      return {
        data: null,
        error:
          (parsed && typeof parsed.error === 'string' ? parsed.error : null) ||
          res.error.message ||
          String(res.error),
      };
    }
    const parsed = parseData(res.data);
    if (!parsed) {
      return { data: null, error: 'Ungültige Server-Antwort' };
    }
    return { data: parsed, error: null };
  } catch (e: unknown) {
    return { data: null, error: errMsg(e) };
  }
}
